import { useState, useCallback } from 'react';

// ---------- Spinner ----------
export function Spinner({ size = 24, color }) {
  return (
    <div style={{
      width: size, height: size, border: '2px solid #E4E7F0',
      borderTopColor: color || 'var(--brand-color)',
      borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block'
    }} />
  );
}

// ---------- Loading page ----------
export function LoadingPage() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 14, flexDirection: 'column' }}>
      <Spinner size={40} />
      <p style={{ color: '#8B94A8', fontSize: 14, fontWeight: 500 }}>Loading…</p>
    </div>
  );
}

// ---------- Empty state ----------
export function EmptyState({ icon = '🔍', title = 'Nothing found', subtitle = '', action }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>{icon}</div>
      <h3 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>{title}</h3>
      {subtitle && <p style={{ color: '#8B94A8', fontSize: 14, marginBottom: 20, maxWidth: 380, margin: '0 auto 20px' }}>{subtitle}</p>}
      {action}
    </div>
  );
}

// ---------- Page header (used inside portal main) ----------
export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="portal-topbar">
      <div>
        <h1 className="portal-heading">{title}</h1>
        {subtitle && <p className="portal-sub">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ---------- Stat card (dashboard) ----------
export function StatCard({ label, value, icon, change, gradient, iconBg }) {
  return (
    <div className="ds-card">
      <div className="ds-card-head">
        <span className="ds-card-label">{label}</span>
        {icon && (
          <span className={`ds-card-icon ${gradient ? 'gradient' : ''}`} style={iconBg ? { background: iconBg, color: '#fff' } : undefined}>
            {icon}
          </span>
        )}
      </div>
      <p
        className={`ds-card-value ${gradient ? 'gradient' : ''}`}
        title={typeof value === 'string' ? value : undefined}
        style={{
          // Long values (e.g. admin.mail_settings.update, full email
          // addresses) were overflowing the Audit Log "Top action" / "Top
          // actor" cards and bleeding into adjacent columns. Truncate to
          // a single line with ellipsis; full value stays available on
          // hover via the title attribute.
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >{value}</p>
      {change !== undefined && change !== null && (
        <span className={`ds-card-delta ${change >= 0 ? 'pos' : 'neg'}`}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% vs last period
        </span>
      )}
    </div>
  );
}

// ---------- Pagination ----------
export function Pagination({ meta, onPageChange }) {
  if (!meta || meta.last_page <= 1) return null;
  const pages = Array.from({ length: meta.last_page }, (_, i) => i + 1);
  const shown = pages.filter(p => p === 1 || p === meta.last_page || Math.abs(p - meta.current_page) <= 2);

  const btnStyle = (active, disabled) => ({
    minWidth: 38, height: 38, padding: '0 12px',
    border: active ? '1.5px solid transparent' : '1.5px solid #E4E7F0',
    borderRadius: 10,
    background: active ? 'var(--brand-gradient)' : '#fff',
    color: active ? '#fff' : disabled ? '#C5CAD9' : '#4A5568',
    fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', transition: 'all 0.15s',
    boxShadow: active ? 'var(--shadow-glow)' : 'none',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 32, flexWrap: 'wrap' }}>
      <button style={btnStyle(false, meta.current_page === 1)} onClick={() => onPageChange(meta.current_page - 1)} disabled={meta.current_page === 1}>‹ Prev</button>
      {shown.map((p, i) => (
        <span key={p} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 && shown[i - 1] !== p - 1 && <span style={{ padding: '0 6px', color: '#8B94A8' }}>…</span>}
          <button style={btnStyle(p === meta.current_page, false)} onClick={() => onPageChange(p)}>{p}</button>
        </span>
      ))}
      <button style={btnStyle(false, meta.current_page === meta.last_page)} onClick={() => onPageChange(meta.current_page + 1)} disabled={meta.current_page === meta.last_page}>Next ›</button>
    </div>
  );
}

// ---------- Search input ----------
export function SearchInput({ value, onChange, placeholder = 'Search deals…', onSubmit }) {
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit && onSubmit(value); }}
      style={{ display: 'flex', background: '#fff', borderRadius: 10, border: '1.5px solid #E4E7F0', overflow: 'hidden' }}
    >
      <span style={{ display: 'flex', alignItems: 'center', padding: '0 8px 0 14px', color: '#8B94A8', fontSize: 16 }}>🔍</span>
      <input
        style={{ flex: 1, padding: '10px 8px', border: 'none', outline: 'none', fontSize: 14, color: '#0F1225', background: 'transparent', minWidth: 0, fontFamily: 'inherit' }}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <button type="submit" className="btn btn-primary" style={{ borderRadius: 0, padding: '10px 16px', fontSize: 13 }}>
        Search
      </button>
    </form>
  );
}

// ---------- Confirm modal ----------
export function ConfirmModal({ open, title, message, onConfirm, onCancel, danger, confirmLabel = 'Confirm' }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: 24, color: '#4A5568', lineHeight: 1.6 }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Promise-shaped confirm dialog, so callers can drop in a styled
 * replacement for `window.confirm()` without restructuring control flow.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete?', message: '...', danger: true }))) return;
 *
 * Returns:
 *   [confirm, modalNode]
 *
 * The caller must render `modalNode` somewhere in its tree (typically at
 * the end of the returned JSX). Each call resolves to true on confirm,
 * false on cancel or backdrop click. The modal mounts/unmounts based on
 * the pending promise — there's no orphan render when nothing is asked.
 */
export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({ opts: opts || {}, resolve });
    });
  }, []);

  const node = state ? (
    <ConfirmModal
      open
      title={state.opts.title || 'Are you sure?'}
      message={state.opts.message || ''}
      danger={state.opts.danger}
      confirmLabel={state.opts.confirmLabel || (state.opts.danger ? 'Delete' : 'Confirm')}
      onConfirm={() => { state.resolve(true);  setState(null); }}
      onCancel={()  => { state.resolve(false); setState(null); }}
    />
  ) : null;

  return [confirm, node];
}
